/**
 * Menu search component.
 */
import Button from '@components/atoms/icons/searchButton';
import Container from '@components/atoms/container/menuSearchContainer';
import Context from '@shared/components/utils/context/menuContext';
import Focus from '@shared/types/enums/focus';
import Pages from '@pages';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { emptyRoute, routerSelector } from '@shared/store/slices/router.slice';
import { focusSelector, setFocus } from '@shared/store/slices/focus.slice';
import { pushRoute } from '@shared/store/thunks/route.thunk';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { userSelector } from '@shared/store/slices/user.slice';
import { DURATION, TRANSITION } from '@shared/types/constants/animations';

/**
 * Width and height of the search button.
 */
const BUTTON_WIDTH = 50;
const BUTTON_HEIGHT = 50;

/**
 * Displays the search button in the menu.
 */
export default function MenuSearch() {
  const { callbacks, props } = useMenuSearch();
  return (
    <Container {...props.container} {...callbacks.container}>
      <Button {...props.button} />
    </Container>
  );
}

/**
 * Hook that manages the display of the search button and the search action.
 */
function useMenuSearch() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Context
  const { collapse, display, effectiveIndex, setListIndex } =
    useContext(Context);

  // Selectors
  const { focus } = useAppSelector(focusSelector);
  const { route } = useAppSelector(routerSelector);
  const { isAnonymous } = useAppSelector(userSelector);

  // Refs
  const displayRef = useRef(display);
  const isAnonymousRef = useRef(isAnonymous);
  const pageRef = useRef(route.Page);
  displayRef.current = display;
  isAnonymousRef.current = isAnonymous;
  pageRef.current = route.Page;

  // ------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------

  /**
   * Navigates to the search page and resets the list.
   */
  const goToSearch = useCallback(() => {
    if (!displayRef.current) return;
    if (pageRef.current === Pages.SEARCH) return;
    setListIndex.current(0);
    setTimeout(() => {
      effectiveIndex.current = -1;
      dispatch(emptyRoute());
      dispatch(setFocus(Focus.SEARCH));
      dispatch(
        pushRoute({
          route: { Page: Pages.SEARCH },
          params: {
            push: true,
          },
        })
      );
    });
  }, []);

  // ------------------------------------------------------------
  // Keyboard hook
  // ------------------------------------------------------------

  useKeyboard({
    focus: Focus.SEARCH,
    keyDownHandler: (key) => {
      if (!displayRef.current) return;
      switch (key) {
        case 'DOWN':
          dispatch(setFocus(Focus.MENU));
          break;
        case 'UP':
          if (!isAnonymousRef.current) return;
          dispatch(setFocus(Focus.LOGIN));
          break;
        case 'SELECT':
          goToSearch();
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  const focused = useMemo(() => {
    return (
      focus === Focus.SEARCH ||
      (pageRef.current === Pages.SEARCH && focus !== Focus.LOGIN)
    );
  }, [focus, pageRef.current]);

  // Return values

  return useMemo(() => {
    return {
      callbacks: {
        container: {
          onClick: goToSearch,
        },
      },
      props: {
        button: {
          collapse,
          focused,
          height: BUTTON_HEIGHT,
          width: BUTTON_WIDTH,
        },
        container: {
          collapse,
          display,
          duration: DURATION.MENU_SEARCH_OPACITY,
          focused,
          transition: TRANSITION.MENU_SEARCH_TRANSLATION,
        },
      },
    };
  }, [collapse, display, focused, goToSearch]);
}
